import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { MapDispatchToProps } from '~commonInfra/utils/ConnectUtils';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '~src/spacesPlatform/actions/filters/FilterActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { getOrderClausesFromQueryParam } from '~src/spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/spacesPlatform/models/sort/SortOptionDefinitions';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { CollectionsSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { ConnectedQueryParamUpdateProvider } from '../../bootstrap/routerHelpers/QueryParamUpdateProvider';
import { AllCollectionsSpaceOwnProps } from './AllCollectionsSpace';
import { ConnectedAllCollectionsSpace } from './ConnectedAllCollectionsSpace';

export interface AllCollectionsSpaceProviderOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: CollectionsSubspaceName;
  readonly renderSpace: (props: AllCollectionsSpaceOwnProps) => JSX.Element;
}

export interface AllCollectionsSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type AllCollectionsSpaceProviderContainerProps = AllCollectionsSpaceProviderOwnProps & AllCollectionsSpaceProviderDispatchProps;

export class AllCollectionsSpaceProviderContainer extends React.PureComponent<AllCollectionsSpaceProviderContainerProps> {
  public render() {
    const props = {
      urls: this.props.urls
    };
    return this.props.renderSpace(props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllCollectionsSpaceProviderContainerProps) {
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'collections',
        subspaceName: this.props.subspaceName
      }
    );

    this.setFilterTypesFromUrl();
    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL() {
    const spaceInfo: SpaceInfo = {
      item: SpaceItemForNonEntitySpace,
      spaceType: 'collections',
      subspaceName: this.props.subspaceName
    };
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(['collection'], spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private setFilterTypesFromUrl() {
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    for (const filterType of _.keys(ContentFiltersKeys)) {
      const value = currentQueryValues[filterType];
      this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value ? value + '' : undefined);
    }
  }
}

const mapDispatchToProps: MapDispatchToProps<AllCollectionsSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  setOrderAction,
  setContentFilterNoRequestContent
}, dispatch);

export const ConnectedAllCollectionsSpaceProviderContainer: React.ComponentClass<AllCollectionsSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(AllCollectionsSpaceProviderContainer);

const renderSpace = (props: AllCollectionsSpaceOwnProps): JSX.Element => <ConnectedAllCollectionsSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type AllCollectionsSpaceProviderProps = LocationPathProp & Omit<AllCollectionsSpaceProviderOwnProps, 'renderSpace'>;

export const AllCollectionsSpaceProvider: React.FunctionComponent<AllCollectionsSpaceProviderProps> = (props) => {

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'collections',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={false}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllCollectionsSpaceProviderContainer
          routeLocationPath={props.routeLocationPath}
          routeLocationSearch={props.routeLocationSearch}
          renderSpace={renderSpace}
          urls={props.urls}
          subspaceName={props.subspaceName}
        />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
